/**
 * PhrasePatternsEditor Component
 * Editable lists for common phrases and phrases to avoid.
 */
import React, { useState } from 'react';

interface PhrasePatternsEditorProps {
  title: string;
  phrases: string[];
  onUpdate: (phrases: string[]) => void;
  variant?: 'positive' | 'negative';
}

const PhrasePatternsEditor: React.FC<PhrasePatternsEditorProps> = ({
  title,
  phrases,
  onUpdate,
  variant = 'positive',
}) => {
  const [newPhrase, setNewPhrase] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAdd = () => {
    const trimmed = newPhrase.trim();
    if (trimmed && !phrases.includes(trimmed)) {
      onUpdate([...phrases, trimmed]);
      setNewPhrase('');
      setIsAdding(false);
    }
  };

  const handleRemove = (phrase: string) => {
    onUpdate(phrases.filter((p) => p !== phrase));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    } else if (e.key === 'Escape') {
      setNewPhrase('');
      setIsAdding(false);
    }
  };

  const colorClasses =
    variant === 'positive'
      ? {
          badge: 'bg-green-50 text-green-700 border-green-200',
          button: 'hover:text-green-900',
        }
      : {
          badge: 'bg-red-50 text-red-700 border-red-200',
          button: 'hover:text-red-900',
        };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">{title}</h3>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add
          </button>
        )}
      </div>

      {/* Add new phrase input */}
      {isAdding && (
        <div className="mb-3 flex items-center gap-2">
          <input
            type="text"
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter a phrase..."
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            autoFocus
          />
          <button
            onClick={handleAdd}
            disabled={!newPhrase.trim()}
            className="p-2 text-green-600 hover:text-green-700 disabled:text-gray-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          <button
            onClick={() => {
              setNewPhrase('');
              setIsAdding(false);
            }}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Phrases list */}
      <ul className="space-y-2">
        {phrases.map((phrase, index) => (
          <li
            key={index}
            className={`flex items-center justify-between px-3 py-2 rounded-md border ${colorClasses.badge}`}
          >
            <span className="text-sm">"{phrase}"</span>
            <button
              onClick={() => handleRemove(phrase)}
              className={`${colorClasses.button}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </li>
        ))}

        {phrases.length === 0 && !isAdding && (
          <li className="text-sm text-gray-400 py-2">No phrases set</li>
        )}
      </ul>
    </div>
  );
};

export default PhrasePatternsEditor;
